import React, { useState, useEffect } from "react";
import { getDatabase, ref, onValue } from "firebase/database";
import {
  Calendar,
  Clock,
  Award,
  TrendingUp,
  BarChart,
  History,
  ArrowLeft,
  Home,
  Trophy,
  Target,
  Percent,
  ArrowDownAZ,
  Filter,
  ChevronDown,
  Star,
  CheckCircle,
  XCircle,
  Shield,
  Bookmark,
  Crown,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

// Función para formatear la fecha
const formatDate = (date) => {
  // Comprueba si la fecha viene en formato DD/MM/YYYY
  if (typeof date === "string" && date.includes("/")) {
    const parts = date.split("/");
    // Si tiene el formato DD/MM/YYYY, no necesita conversión adicional
    if (parts.length === 3) {
      return date;
    }
  }

  // Si no tiene el formato esperado, intentamos formatearla
  try {
    const options = {
      day: "numeric",
      month: "long",
      year: "numeric",
      timeZone: "America/Bogota",
    };
    return new Date(date).toLocaleDateString("es-ES", options);
  } catch (error) {
    return date || "Fecha no disponible";
  }
};

// Componente para métricas de rendimiento
const PerformanceMetrics = ({ history }) => {
  // Calcular estadísticas
  const totalBets = history.length;
  const won = history.filter((bet) => bet.state === "ganada").length;
  const winRate = totalBets > 0 ? Math.round((won / totalBets) * 100) : 0;

  // Calcular rentabilidad
  const profitability = history.reduce((total, bet) => {
    if (bet.state === "ganada" && bet.odds) {
      return total + (parseFloat(bet.odds) - 1);
    } else if (bet.state === "perdida") {
      return total - 1;
    }
    return total;
  }, 0);

  const profitabilityPercent =
    totalBets > 0 ? Math.round((profitability / totalBets) * 100) : 0;

  const metrics = [
    {
      title: "Pronósticos",
      value: totalBets,
      icon: <Target className="h-5 w-5" />,
      color: "text-blue-400",
    },
    {
      title: "Victorias",
      value: won,
      icon: <Trophy className="h-5 w-5" />,
      color: "text-green-400",
    },
    {
      title: "Efectividad",
      value: `${winRate}%`,
      icon: <Percent className="h-5 w-5" />,
      color: "text-yellow-400",
    },
    {
      title: "Rentabilidad",
      value: `${profitabilityPercent > 0 ? "+" : ""}${profitabilityPercent}%`,
      icon: <TrendingUp className="h-5 w-5" />,
      color: profitabilityPercent >= 0 ? "text-green-400" : "text-red-400",
    },
  ];

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
      {metrics.map((metric, index) => (
        <div
          key={index}
          className="bg-gray-800 rounded-xl p-4 shadow-lg border border-gray-700 transform transition-all duration-300 hover:scale-105 hover:shadow-xl hover:border-gray-600"
        >
          <div className="flex items-center justify-between mb-2">
            <span className="text-gray-400 font-medium text-sm">
              {metric.title}
            </span>
            <div className={`${metric.color} bg-gray-900 p-2 rounded-lg`}>
              {metric.icon}
            </div>
          </div>
          <div className="text-white text-xl font-bold">{metric.value}</div>
        </div>
      ))}
    </div>
  );
};

// Componente para cada tarjeta de historial
const BetHistoryCard = ({ prediction }) => {
  // Determinar el color del estado
  let statusColor, statusText, statusIcon, statusBg;
  switch (prediction.state) {
    case "ganada":
      statusColor = "text-green-400";
      statusBg = "bg-green-900";
      statusText = "Ganada";
      statusIcon = <Award className="w-4 h-4" />;
      break;
    case "perdida":
      statusColor = "text-red-400";
      statusBg = "bg-red-900";
      statusText = "Perdida";
      statusIcon = <XCircle className="w-4 h-4" />;
      break;
    case "nula":
      statusColor = "text-gray-400";
      statusBg = "bg-gray-700";
      statusText = "Nula";
      statusIcon = <BarChart className="w-4 h-4" />;
      break;
    default:
      statusColor = "text-gray-400";
      statusBg = "bg-gray-700";
      statusText = "Desconocido";
      statusIcon = <History className="w-4 h-4" />;
  }

  // Nivel de confianza
  const confidenceLevel = prediction.confidence || 0;
  let confidenceColor;
  if (confidenceLevel >= 80) {
    confidenceColor = "bg-green-500";
  } else if (confidenceLevel >= 60) {
    confidenceColor = "bg-green-600";
  } else if (confidenceLevel >= 40) {
    confidenceColor = "bg-yellow-500";
  } else if (confidenceLevel >= 20) {
    confidenceColor = "bg-orange-500";
  } else {
    confidenceColor = "bg-red-500";
  }

  // Determinar si es VIP o Free
  const isVip = prediction.source === "abetvip";
  const planBadge = isVip ? (
    <div className="absolute top-3 left-3 bg-yellow-900 text-yellow-300 px-3 py-1 rounded-lg flex items-center space-x-1 shadow-md">
      <Crown className="w-4 h-4" />
      <span className="text-sm font-medium ml-1">VIP</span>
    </div>
  ) : (
    <div className="absolute top-3 left-3 bg-blue-900 text-blue-300 px-3 py-1 rounded-lg flex items-center space-x-1 shadow-md">
      <Bookmark className="w-4 h-4" />
      <span className="text-sm font-medium ml-1">FREE</span>
    </div>
  );

  return (
    <div className="w-full bg-gray-800 rounded-xl shadow-lg mb-4 overflow-hidden hover:shadow-blue-900/20 transition-all duration-300 border border-gray-700 hover:border-gray-600">
      <div className="relative">
        {/* Indicador de Plan (VIP o Free) */}
        {planBadge}

        {/* Indicador de Estado */}
        <div
          className={`absolute top-3 right-3 ${statusBg} ${statusColor} px-3 py-1 rounded-lg flex items-center space-x-1 shadow-md`}
        >
          {statusIcon}
          <span className="text-sm font-medium ml-1">{statusText}</span>
        </div>

        {/* Contenido Principal */}
        <div className="p-5">
          {/* Equipos con logos */}
          <div className="flex items-center justify-between mb-6 mt-8">
            <div className="flex items-center flex-1">
              <div className="bg-gray-700 rounded-full p-2 mr-3 shadow-md">
                {prediction.homeTeam?.logo ? (
                  <img
                    src={prediction.homeTeam.logo}
                    alt={prediction.homeTeam.name}
                    className="w-10 h-10 object-contain"
                  />
                ) : (
                  <Shield className="w-10 h-10 text-gray-500" />
                )}
              </div>
              <span className="font-semibold text-white">
                {prediction.homeTeam?.name || "Equipo Local"}
              </span>
            </div>

            <div className="mx-4 text-gray-400 font-bold text-lg">VS</div>

            <div className="flex items-center flex-1 justify-end">
              <span className="font-semibold text-white">
                {prediction.awayTeam?.name || "Equipo Visitante"}
              </span>
              <div className="bg-gray-700 rounded-full p-2 ml-3 shadow-md">
                {prediction.awayTeam?.logo ? (
                  <img
                    src={prediction.awayTeam.logo}
                    alt={prediction.awayTeam.name}
                    className="w-10 h-10 object-contain"
                  />
                ) : (
                  <Shield className="w-10 h-10 text-gray-500" />
                )}
              </div>
            </div>
          </div>

          {/* Fecha, Hora y Cuota */}
          <div className="flex justify-between items-center mb-5 text-sm">
            <div className="flex items-center text-gray-400">
              <Calendar className="w-4 h-4 mr-1" />
              <span>{formatDate(prediction.date)}</span>
              {prediction.hours && (
                <>
                  <Clock className="w-4 h-4 ml-3 mr-1" />
                  <span>{prediction.hours}</span>
                </>
              )}
            </div>
            <div className="bg-blue-900 text-blue-300 font-bold rounded-lg px-4 py-1 flex items-center shadow-inner">
              <Star className="w-4 h-4 mr-1 text-yellow-400" />
              {typeof prediction.odds === "number"
                ? prediction.odds.toFixed(2)
                : prediction.odds || "1.00"}
            </div>
          </div>

          {/* Predicción */}
          <div className="bg-gray-900 rounded-lg p-4 mb-4 border border-gray-700">
            <div className="font-medium text-gray-400 mb-1">Predicción:</div>
            <div className="text-white font-bold">
              {prediction.prediction || "No disponible"}
            </div>
          </div>

          {/* Nivel de confianza */}
          {typeof prediction.confidence === "number" && (
            <div className="mt-4">
              <div className="flex justify-between mb-2">
                <span className="text-xs font-medium text-gray-400">
                  Nivel de Confianza
                </span>
                <span className="text-xs font-bold text-white">
                  {prediction.confidence}%
                </span>
              </div>
              <div className="w-full bg-gray-700 rounded-full h-2.5">
                <div
                  className={`${confidenceColor} h-2.5 rounded-full shadow-lg transition-all duration-500 ease-in-out`}
                  style={{ width: `${prediction.confidence}%` }}
                ></div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Componente principal
const BetHistoryDashboard = ({ userUUID }) => {
  const [history, setHistory] = useState([]);
  const [activeTab, setActiveTab] = useState("all");
  const [activePlan, setActivePlan] = useState("all"); // Nuevo estado para el filtro de plan
  const [isLoading, setIsLoading] = useState(true);
  const [sortBy, setSortBy] = useState("newest");
  const [showSortMenu, setShowSortMenu] = useState(false);
  const navigate = useNavigate();

  const handleBack = () => {
    navigate(-1);
  };

  useEffect(() => {
    const db = getDatabase();
    const abetRef = ref(db, "abet");
    const abetVipRef = ref(db, "abetvip");
    const loadedHistory = [];
    let loadedCount = 0;

    setIsLoading(true);

    // Cargar los pronósticos FREE desde Firebase
    onValue(abetRef, (snapshot) => {
      const data = snapshot.val() || {};
      const freeHistory = Object.keys(data).map((key) => ({
        id: key,
        ...data[key],
        source: "abet", // Marcamos la fuente
      }));

      // Filtrar por userUUID si está presente
      const filteredFreeHistory = userUUID
        ? freeHistory.filter((prediction) => prediction.userUUID === userUUID)
        : freeHistory;

      // Filtrar solo los ganados y perdidos
      const finalFreeHistory = filteredFreeHistory.filter(
        (prediction) =>
          prediction.state === "ganada" ||
          prediction.state === "perdida" ||
          prediction.state === "nula"
      );

      loadedHistory.push(...finalFreeHistory);
      loadedCount++;

      // Solo actualizamos el estado cuando tengamos ambos datos
      if (loadedCount === 2) {
        setHistory(loadedHistory);
        setIsLoading(false);
      }
    });

    // Cargar los pronósticos VIP desde Firebase
    onValue(abetVipRef, (snapshot) => {
      const data = snapshot.val() || {};
      const vipHistory = Object.keys(data).map((key) => ({
        id: key,
        ...data[key],
        source: "abetvip", // Marcamos la fuente
      }));

      // Filtrar por userUUID si está presente
      const filteredVipHistory = userUUID
        ? vipHistory.filter((prediction) => prediction.userUUID === userUUID)
        : vipHistory;

      // Filtrar solo los ganados y perdidos
      const finalVipHistory = filteredVipHistory.filter(
        (prediction) =>
          prediction.state === "ganada" ||
          prediction.state === "perdida" ||
          prediction.state === "nula"
      );

      loadedHistory.push(...finalVipHistory);
      loadedCount++;

      // Solo actualizamos el estado cuando tengamos ambos datos
      if (loadedCount === 2) {
        setHistory(loadedHistory);
        setIsLoading(false);
      }
    });
  }, [userUUID]);

  // Función para filtrar el historial según la pestaña activa y el plan activo
  const getFilteredHistory = () => {
    let filtered = [...history];

    // Filtrar por estado (ganada, perdida, nula)
    switch (activeTab) {
      case "won":
        filtered = filtered.filter(
          (prediction) => prediction.state === "ganada"
        );
        break;
      case "lost":
        filtered = filtered.filter(
          (prediction) => prediction.state === "perdida"
        );
        break;
      case "null":
        filtered = filtered.filter((prediction) => prediction.state === "nula");
        break;
      // Eliminamos el caso "pending" ya que no queremos mostrar los pendientes
    }

    // Filtrar por plan (VIP o FREE)
    switch (activePlan) {
      case "free":
        filtered = filtered.filter(
          (prediction) => prediction.source === "abet"
        );
        break;
      case "vip":
        filtered = filtered.filter(
          (prediction) => prediction.source === "abetvip"
        );
        break;
      // El caso "all" no necesita filtrado adicional
    }

    // Ordenar por fecha
    return filtered.sort((a, b) => {
      const dateA = new Date(a.date);
      const dateB = new Date(b.date);

      if (sortBy === "newest") {
        return dateB - dateA;
      } else {
        return dateA - dateB;
      }
    });
  };

  // Obtener el conteo para cada categoría
  const counts = {
    all: history.length,
    won: history.filter((prediction) => prediction.state === "ganada").length,
    lost: history.filter((prediction) => prediction.state === "perdida").length,
    null: history.filter((prediction) => prediction.state === "nula").length,
    free: history.filter((prediction) => prediction.source === "abet").length,
    vip: history.filter((prediction) => prediction.source === "abetvip").length,
  };

  return (
    <div className="min-h-screen bg-gray-950 text-white">
      {/* Header */}
      <header className="bg-gray-900 border-b border-gray-800 shadow-lg sticky top-0 z-10">
        <div className="container mx-auto px-4 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center">
              <button
                onClick={handleBack}
                className="mr-4 bg-gray-800 hover:bg-gray-700 rounded-full p-2 transition-colors duration-200"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div className="flex items-center">
                <Trophy className="w-6 h-6 text-blue-400 mr-2" />
                <h1 className="text-xl font-bold text-white">DeportivosPro</h1>
              </div>
            </div>
            <button
              onClick={handleBack}
              className="bg-gray-800 hover:bg-gray-700 rounded-full p-2 transition-colors duration-200"
            >
              <Home className="w-5 h-5" />
            </button>
          </div>
        </div>
      </header>

      <div className="container mx-auto p-4">
        {/* Titulo y filtros */}
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between mb-6 mt-4">
          <h2 className="text-2xl font-bold text-white flex items-center mb-4 md:mb-0">
            <History className="w-6 h-6 mr-2 text-blue-400" />
            Historial de Pronósticos
          </h2>

          {/* Botón de ordenar */}
          <div className="relative">
            <button
              onClick={() => setShowSortMenu(!showSortMenu)}
              className="flex items-center space-x-2 bg-gray-800 hover:bg-gray-700 text-white px-4 py-2 rounded-lg transition-colors duration-200 shadow-md"
            >
              <ArrowDownAZ className="w-4 h-4" />
              <span>
                Ordenar por:{" "}
                {sortBy === "newest" ? "Más recientes" : "Más antiguos"}
              </span>
              <ChevronDown className="w-4 h-4" />
            </button>

            {showSortMenu && (
              <div className="absolute right-0 mt-2 w-48 bg-gray-800 rounded-lg shadow-xl border border-gray-700 z-10">
                <button
                  className="w-full text-left px-4 py-2 hover:bg-gray-700 rounded-t-lg flex items-center"
                  onClick={() => {
                    setSortBy("newest");
                    setShowSortMenu(false);
                  }}
                >
                  {sortBy === "newest" && (
                    <CheckCircle className="w-4 h-4 mr-2 text-blue-400" />
                  )}
                  <span className={sortBy === "newest" ? "ml-6" : ""}>
                    Más recientes
                  </span>
                </button>
                <button
                  className="w-full text-left px-4 py-2 hover:bg-gray-700 rounded-b-lg flex items-center"
                  onClick={() => {
                    setSortBy("oldest");
                    setShowSortMenu(false);
                  }}
                >
                  {sortBy === "oldest" && (
                    <CheckCircle className="w-4 h-4 mr-2 text-blue-400" />
                  )}
                  <span className={sortBy === "oldest" ? "ml-6" : ""}>
                    Más antiguos
                  </span>
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Métricas de rendimiento */}
        <PerformanceMetrics history={history} />

        {/* Filtro de Plan (VIP/FREE) */}
        <div className="flex overflow-x-auto mb-4 bg-gray-900 rounded-xl shadow-md border border-gray-800">
          <button
            onClick={() => setActivePlan("all")}
            className={`flex-1 py-3 px-4 text-center relative ${
              activePlan === "all"
                ? "text-blue-400 font-bold"
                : "text-gray-400 hover:text-blue-300"
            } transition-colors duration-200`}
          >
            <div className="flex items-center justify-center">
              <Filter className="w-4 h-4 mr-2" />
              <span>Todos</span>
              <span className="ml-2 bg-gray-800 text-gray-300 rounded-full px-2 py-0.5 text-xs">
                {counts.all}
              </span>
            </div>
            {activePlan === "all" && (
              <div className="absolute bottom-0 left-0 w-full h-1 bg-blue-500 animate-pulse"></div>
            )}
          </button>
          <button
            onClick={() => setActivePlan("free")}
            className={`flex-1 py-3 px-4 text-center relative ${
              activePlan === "free"
                ? "text-blue-400 font-bold"
                : "text-gray-400 hover:text-blue-300"
            } transition-colors duration-200`}
          >
            <div className="flex items-center justify-center">
              <Bookmark className="w-4 h-4 mr-2" />
              <span>FREE</span>
              <span className="ml-2 bg-blue-900 text-blue-300 rounded-full px-2 py-0.5 text-xs">
                {counts.free}
              </span>
            </div>
            {activePlan === "free" && (
              <div className="absolute bottom-0 left-0 w-full h-1 bg-blue-500 animate-pulse"></div>
            )}
          </button>
          <button
            onClick={() => setActivePlan("vip")}
            className={`flex-1 py-3 px-4 text-center relative ${
              activePlan === "vip"
                ? "text-yellow-400 font-bold"
                : "text-gray-400 hover:text-yellow-300"
            } transition-colors duration-200`}
          >
            <div className="flex items-center justify-center">
              <Crown className="w-4 h-4 mr-2" />
              <span>VIP</span>
              <span className="ml-2 bg-yellow-900 text-yellow-300 rounded-full px-2 py-0.5 text-xs">
                {counts.vip}
              </span>
            </div>
            {activePlan === "vip" && (
              <div className="absolute bottom-0 left-0 w-full h-1 bg-yellow-500 animate-pulse"></div>
            )}
          </button>
        </div>

        {/* Tabs de Estado */}
        <div className="flex overflow-x-auto mb-6 bg-gray-900 rounded-xl shadow-md border border-gray-800">
          <button
            onClick={() => setActiveTab("all")}
            className={`flex-1 py-4 px-4 text-center relative ${
              activeTab === "all"
                ? "text-blue-400 font-bold"
                : "text-gray-400 hover:text-blue-300"
            } transition-colors duration-200`}
          >
            <div className="flex items-center justify-center">
              <Filter className="w-4 h-4 mr-2" />
              <span>Todos</span>
              <span className="ml-2 bg-gray-800 text-gray-300 rounded-full px-2 py-0.5 text-xs">
                {counts.all}
              </span>
            </div>
            {activeTab === "all" && (
              <div className="absolute bottom-0 left-0 w-full h-1 bg-blue-500 animate-pulse"></div>
            )}
          </button>
          <button
            onClick={() => setActiveTab("won")}
            className={`flex-1 py-4 px-4 text-center relative ${
              activeTab === "won"
                ? "text-green-400 font-bold"
                : "text-gray-400 hover:text-green-300"
            } transition-colors duration-200`}
          >
            <div className="flex items-center justify-center">
              <CheckCircle className="w-4 h-4 mr-2" />
              <span>Ganados</span>
              <span className="ml-2 bg-green-900 text-green-300 rounded-full px-2 py-0.5 text-xs">
                {counts.won}
              </span>
            </div>
            {activeTab === "won" && (
              <div className="absolute bottom-0 left-0 w-full h-1 bg-green-500 animate-pulse"></div>
            )}
          </button>
          <button
            onClick={() => setActiveTab("lost")}
            className={`flex-1 py-4 px-4 text-center relative ${
              activeTab === "lost"
                ? "text-red-400 font-bold"
                : "text-gray-400 hover:text-red-300"
            } transition-colors duration-200`}
          >
            <div className="flex items-center justify-center">
              <XCircle className="w-4 h-4 mr-2" />
              <span>Perdidos</span>
              <span className="ml-2 bg-red-900 text-red-300 rounded-full px-2 py-0.5 text-xs">
                {counts.lost}
              </span>
            </div>
            {activeTab === "lost" && (
              <div className="absolute bottom-0 left-0 w-full h-1 bg-red-500 animate-pulse"></div>
            )}
          </button>
          <button
            onClick={() => setActiveTab("null")}
            className={`flex-1 py-4 px-4 text-center relative ${
              activeTab === "null"
                ? "text-gray-300 font-bold"
                : "text-gray-400 hover:text-gray-300"
            } transition-colors duration-200`}
          >
            <div className="flex items-center justify-center">
              <BarChart className="w-4 h-4 mr-2" />
              <span>Nulos</span>
              <span className="ml-2 bg-gray-700 text-gray-300 rounded-full px-2 py-0.5 text-xs">
                {counts.null}
              </span>
            </div>
            {activeTab === "null" && (
              <div className="absolute bottom-0 left-0 w-full h-1 bg-gray-500 animate-pulse"></div>
            )}
          </button>
        </div>

        {/* Resultados */}
        <div className="space-y-4">
          {isLoading ? (
            <div className="flex justify-center items-center py-16">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
            </div>
          ) : getFilteredHistory().length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {getFilteredHistory().map((prediction) => (
                <BetHistoryCard key={prediction.id} prediction={prediction} />
              ))}
            </div>
          ) : (
            <div className="bg-gray-800 p-10 rounded-xl shadow-lg text-center border border-gray-700">
              <History className="w-16 h-16 mx-auto text-gray-600 mb-4" />
              <p className="text-gray-400 text-lg mb-2">
                No hay pronósticos en esta categoría.
              </p>
              <p className="text-gray-500">
                Los pronósticos aparecerán aquí cuando estén disponibles.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BetHistoryDashboard;
